/**
 * Super Admin — Users Management Page
 *
 * Shows all admin/staff users (super_admin, clinic_admin, credit_admin, employee).
 * Doctors are managed at /superadmin/doctors. Mothers at /superadmin/mothers.
 *
 * Key feature: "Yeni Kullanıcı Ekle" dialog with role select that INCLUDES
 * credit_admin — so the super admin can finally create credit-admin accounts
 * from the UI.
 *
 * Backend: POST /api/auth/register-user (super_admin guarded).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../../components/layout';
import { authApi, fetchWithAuth } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  UserPlus,
  Loader2,
  Search,
  Users as UsersIcon,
  ShieldCheck,
  Coins,
  Briefcase,
  Building2,
} from 'lucide-react';

// Role meta — Turkish labels, NO veterinary references.
const ROLE_META = {
  super_admin: {
    label: 'Süper Admin',
    description: 'Tüm sistem yetkisi',
    icon: ShieldCheck,
    cls: 'bg-rose-50 text-rose-700 border-rose-200',
  },
  clinic_admin: {
    label: 'Klinik Yöneticisi',
    description: 'Klinik içi yönetim',
    icon: Building2,
    cls: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  },
  credit_admin: {
    label: 'Kredi Yöneticisi',
    description: 'Doktorlara 1-5 kredi tanımlayabilir',
    icon: Coins,
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  employee: {
    label: 'Çalışan',
    description: 'Klinik personeli',
    icon: Briefcase,
    cls: 'bg-slate-50 text-slate-700 border-slate-200',
  },
  doctor: {
    label: 'Doktor',
    description: 'Obstetri uzmanı',
    icon: UsersIcon,
    cls: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  mother: {
    label: 'Anne',
    description: 'B2C kullanıcı',
    icon: UsersIcon,
    cls: 'bg-pink-50 text-pink-700 border-pink-200',
  },
};

// Roles allowed to be created from this admin/staff page.
// Doctors are public-register and have a dedicated /superadmin/doctors page.
// Mothers are public-register on the mom app side.
const CREATABLE_ROLES = ['credit_admin', 'clinic_admin', 'employee', 'super_admin'];

const ROLE_FILTER_OPTIONS = [
  { value: '', label: 'Tüm yönetici/personel' },
  { value: 'super_admin', label: 'Süper Admin' },
  { value: 'clinic_admin', label: 'Klinik Yöneticisi' },
  { value: 'credit_admin', label: 'Kredi Yöneticisi' },
  { value: 'employee', label: 'Çalışan' },
];

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Reuse the existing super-admin user listing endpoint.
      // Backend currently doesn't have a dedicated /admin/users route, so we
      // pull from /api/admin/credits/users (which lists everyone with a credit
      // account) for doctors/moms and combine with a direct users.find for
      // admin/staff roles. Simpler: call a dedicated endpoint if available.
      // We use a thin GET on /api/admin/users which exists on the backend.
      const res = await fetchWithAuth(
        `/admin/users${roleFilter ? `?role=${roleFilter}` : ''}`,
      );
      let arr = res.users || res.items || [];
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        arr = arr.filter(
          (u) =>
            (u.email || '').toLowerCase().includes(q) ||
            (u.name || '').toLowerCase().includes(q),
        );
      }
      // If the backend returns ALL users incl. doctor/mother, narrow to admin/staff:
      arr = arr.filter((u) =>
        ['super_admin', 'clinic_admin', 'credit_admin', 'employee'].includes(u.role),
      );
      setItems(arr);
    } catch (e) {
      toast.error(e?.message || 'Yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [roleFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="super-admin-users-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <UsersIcon className="h-6 w-6 text-indigo-600" />
              Yöneticiler & Personel
            </h1>
            <p className="text-sm text-slate-500">
              Süper admin, klinik yöneticisi, kredi yöneticisi ve çalışanları yönetin.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} data-testid="users-add-btn">
            <UserPlus className="h-4 w-4 mr-1" /> Yeni Kullanıcı Ekle
          </Button>
        </div>

        <Card>
          <CardContent className="py-3 flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Ara (e-posta veya isim)</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  placeholder="örn. ahmet"
                  data-testid="users-search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Rol</Label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
                data-testid="users-role-filter"
              >
                {ROLE_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <Button onClick={load} disabled={loading} variant="outline" data-testid="users-refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ara'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Toplam {items.length} kullanıcı</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-posta / İsim</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Klinik</TableHead>
                  <TableHead>Aktif</TableHead>
                  <TableHead>Oluşturuldu</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-6">
                      <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Yükleniyor…
                    </TableCell>
                  </TableRow>
                )}
                {!loading && !items.length && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-6">
                      Yönetici/personel hesabı bulunamadı. Sağ üstten <strong>Yeni Kullanıcı Ekle</strong>'ye tıklayın.
                    </TableCell>
                  </TableRow>
                )}
                {items.map((u) => {
                  const meta = ROLE_META[u.role] || { label: u.role, cls: '' };
                  const Icon = meta.icon || UsersIcon;
                  return (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell>
                        <div className="font-medium text-sm">{u.email}</div>
                        <div className="text-xs text-slate-500">{u.name}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={meta.cls}>
                          <Icon className="h-3 w-3 mr-1" />
                          {meta.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {u.clinic_id ? u.clinic_id.slice(0, 8) + '…' : '—'}
                      </TableCell>
                      <TableCell>
                        {u.is_active ? (
                          <Badge variant="outline">aktif</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-slate-100 text-slate-500">
                            pasif
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {(u.created_at || '').split('T')[0]}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {creating && (
          <CreateUserDialog onClose={() => setCreating(false)} onCreated={load} />
        )}
      </div>
    </DashboardLayout>
  );
}

function CreateUserDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'credit_admin', // sensible default since this was the gap
    clinic_id: '',
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error('E-posta, şifre ve isim zorunlu.');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı.');
      return;
    }
    setBusy(true);
    try {
      await authApi.registerUser({
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        role: form.role,
        clinic_id: form.clinic_id ? form.clinic_id.trim() : null,
      });
      toast.success(`Kullanıcı oluşturuldu (${ROLE_META[form.role]?.label || form.role}).`);
      onCreated && onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.message || 'Oluşturulamadı.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="create-user-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Yeni Yönetici / Personel Ekle</DialogTitle>
          <DialogDescription>
            Doktor kayıt akışı ve anne kayıt akışı ayrıdır. Buradan yalnızca yönetici/personel rolleri oluşturulur.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Rol</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {CREATABLE_ROLES.map((r) => {
                const meta = ROLE_META[r];
                const Icon = meta.icon || UsersIcon;
                const selected = form.role === r;
                return (
                  <button
                    type="button"
                    key={r}
                    onClick={() => set('role', r)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      selected
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`role-option-${r}`}
                  >
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Icon className="h-3.5 w-3.5" />
                      {meta.label}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{meta.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs">İsim</Label>
            <Input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              data-testid="create-user-name"
            />
          </div>
          <div>
            <Label className="text-xs">E-posta</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              data-testid="create-user-email"
            />
          </div>
          <div>
            <Label className="text-xs">Şifre (en az 6 karakter)</Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              data-testid="create-user-password"
            />
          </div>
          {(form.role === 'clinic_admin' || form.role === 'employee') && (
            <div>
              <Label className="text-xs">Klinik ID (opsiyonel)</Label>
              <Input
                value={form.clinic_id}
                onChange={(e) => set('clinic_id', e.target.value)}
                placeholder="örn. UUID"
                data-testid="create-user-clinic-id"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            İptal
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="create-user-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Oluştur'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
